import React, { useState, useContext } from 'react';
import { Box, Typography, LinearProgress, Checkbox, Button, Snackbar, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faEraser } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../AuthContext';
import ConfirmDialog from '../common/ConfirmationModal';
import { getCompanyDevices, editLastLogin, requestOperation, checkValid  } from '../../apis/axios';
import {formatDrive} from '../../apis/agent'
export default function DriveItem({ name, label, used, total, status, uuid, sys_uuid, device_id, licence_id ,deletion ,enc_dec  }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  const percentage = Math.round((used / total) * 100);

  const [openRequest, setOpenRequest] = useState(false);
  const [isWiping, setIsWiping] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [wipeStatus, setWipeStatus] = useState(null);
  const [openWipeConfirm, setOpenWipeConfirm] = useState(false);
  //console.log(licence_id)
    const checkLicence=async(data)=>{
      const res=await checkValid({licence_id:licence_id,operation:data})
      if (res.data.status === "expired") {
      setSnackbarOpen(true)
          snackbarMsg("This license has expired.");
          return;
        }
  
        if (res.data.status === "not_allowed") {
           setSnackbarOpen(true)
          snackbarMsg("Operation is not permitted for this license.");
          return;
        }
  
        if (res.data.status !== "allowed") {
           setSnackbarOpen(true)
          snackbarMsg("Invalid license response.");
          return;
        }
    }

  const statusColors = {
    Healthy: { bg: theme.palette.success.light, color: theme.palette.success.main },
    Warning: { bg: theme.palette.warning.light, color: theme.palette.warning.main },
    Critical: { bg: theme.palette.error.light, color: theme.palette.error.main },
  };
  const currentStatus = statusColors[status] || { bg: theme.palette.grey[200], color: theme.palette.text.primary };

  if (!auth.isLoggedIn || !auth.user) {
    return (
      <Box
        border={1}
        borderColor="grey.300"
        borderRadius={2}
        p={2}
        width="100%"
        display="flex"
        justifyContent="center"
      >
        <Typography color="error" textAlign="center">
          User not logged in. Please log in to access drive actions.
        </Typography>
      </Box>
    );
  }

  const handleViewFiles = () => {
    const explorerParams = { drive: name, uuid, sys_uuid, device_id, licence_id ,deletion ,enc_dec };
    //localStorage.setItem('fileExplorerParams', JSON.stringify(explorerParams));
    navigate('/file-explorer', { state: explorerParams });
  };

  const initiateWipeDrive = () => {
     checkLicence("format_drive")
    setOpenWipeConfirm(true);
  };

  const handleConfirmWipe = async () => {
    setOpenWipeConfirm(false);
    setIsWiping(true);
    setWipeStatus(null);

    try {
      let driveLetter = name.replace(/\/$/, '').toUpperCase();
      if (!driveLetter.endsWith(':')) driveLetter += ':';
      driveLetter = driveLetter.replace(/\\/g, '\\\\').replace(/:/g, ':\\\\');

      // Device check logic
      let isActive = true;
      try {
        const companyId = auth.admin || '';
        const devicesResp = await getCompanyDevices({ company_id: companyId });
        const deviceInfo = devicesResp.data.find((dev) => dev.uuid === uuid);
        const statusResp = await editLastLogin({ id: device_id, uuid });
        isActive = statusResp.data.isActive === 1;
      } catch {
        isActive = false;
      }

      const operationData = {
        device_id: parseInt(device_id),
        user_id: parseInt(auth.user),
        type: 'format_drive',
        json: { path: driveLetter },
      };

      if (isActive) {
        const operationResp = await requestOperation(operationData);
        await formatDrive(uuid, driveLetter.replace(/\\\\/g, ''), operationResp.data.log_id, sys_uuid);
        setWipeStatus({ type: 'success', message: `Drive ${name} request Sent to admin.` });
      } else {
        const queueResp = await requestOperation(operationData);
        setWipeStatus({ type: 'info', message: 'Device is offline. Wipe operation has been queued.' });
      }
    } catch (err) {
      setWipeStatus({ type: 'error', message: `Failed to wipe drive ${name}: ${err.message}` });
    } finally {
      setIsWiping(false);
    }
  };

  const handleCancelWipe = () => setOpenWipeConfirm(false);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <Box
      border={1}
      borderColor="grey.300"
      borderRadius={2}
      p={2}
      width="100%"
      position="relative"
      sx={{ opacity: isWiping ? 0.5 : 1 }}
    >
      {isWiping && (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255,255,255,0.6)"
          zIndex={10}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              border: `4px solid ${theme.palette.warning.main}`,
              borderTop: '4px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
            }}
          />
        </Box>
      )}

      {/* Drive Info */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="start" gap={2}>
        <Box display="flex" gap={2} alignItems="start">
          <Checkbox disabled={isWiping} />
          <Box>
            <Typography fontSize={20} fontWeight={700} color="text.primary">
              {name}
            </Typography>
            <Box display="flex" gap={2} alignItems="center" mt={0.5}>
              <Typography fontSize={14} fontWeight={600} color="text.secondary">
                {label}
              </Typography>
              <Box
                sx={{
                  width: 94,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '9999px',
                  fontSize: 14,
                  fontWeight: 600,
                  bgcolor: currentStatus.bg,
                  color: currentStatus.color,
                }}
              >
                {status}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} mt={{ xs: 1, sm: 0 }}>
          <Button
            size="small"
            startIcon={<FontAwesomeIcon icon={faFolder} />}
            onClick={handleViewFiles}
            disabled={isWiping}
          >
            View Files
          </Button>
         {deletion!==0 && (
           <Button
            size="small"
            startIcon={<FontAwesomeIcon icon={faEraser} />}
            onClick={initiateWipeDrive}
            disabled={isWiping}
          >
            Wipe Drive
          </Button>
         )}
        </Box>
      </Box>

      {/* Used Space */}
      <Typography mt={1} fontSize={14} fontWeight={600} color="text.secondary">
        Used Space: {used}GB of {total}GB
      </Typography>

      {/* Progress */}
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={percentage < 50 ? 'success' : percentage < 80 ? 'warning' : 'error'}
        sx={{ height: 6, borderRadius: 4, mt: 1 }}
      />

      {/* Wipe Status */}
      {wipeStatus && (
        <Typography
          mt={1}
          fontSize={14}
          fontWeight={600}
          color={
            wipeStatus.type === 'success'
              ? 'success.main'
              : wipeStatus.type === 'info'
              ? 'warning.main'
              : 'error.main'
          }
          textAlign="center"
        >
          {wipeStatus.message}
        </Typography>
      )}

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={openWipeConfirm}
        title="Confirm Wipe Drive"
        message={`Are you sure you want to wipe drive ${name}? This action will permanently erase all data and cannot be undone.`}
        onConfirm={handleConfirmWipe}
        onCancel={handleCancelWipe}
        confirmText="Wipe"
        cancelText="Cancel"
        confirmButtonColor={theme.palette.error.main}
      />
      <ConfirmDialog
        open={openRequest}
        title="Request for Deletion"
        message="Deletion Request is sent to Admin, You will receive OTP shortly."
        onConfirm={() => setOpenRequest(false)}
        onCancel={() => setOpenRequest(false)}
        confirmText="OK"
        cancelText="Cancel"
        confirmButtonColor={theme.palette.warning.main}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
